import { PHONE_COUNTRY_ROWS } from './countries-data';

/**
 * Países e indicativos para el campo de WhatsApp/teléfono del checkout.
 *
 * Antes del 14-oct-2026 el selector tenía 18 países fijos: clientes de Países Bajos,
 * Guatemala, Polonia, Uruguay, Suiza, Israel, Honduras… no encontraban el suyo (un cliente
 * de Países Bajos lo escribió al bot: «I can not fill in my phone details (NL)»). La tabla
 * completa sale de los metadatos de libphonenumber-js (ver countries-data.ts) y no carga la
 * librería en la página.
 */

export interface PhoneCountry {
  /** ISO 3166-1 alfa-2 */
  iso: string;
  /** Indicativo sin «+» */
  dial: string;
  /** País principal de su indicativo (EE. UU. para +1, Reino Unido para +44…) */
  main: boolean;
}

export const PHONE_COUNTRIES: readonly PhoneCountry[] = PHONE_COUNTRY_ROWS.map(([iso, dial, main]) => ({
  iso,
  dial,
  main: Boolean(main),
}));

const BY_ISO = new Map(PHONE_COUNTRIES.map((c) => [c.iso, c]));
const MAIN_BY_DIAL = new Map(PHONE_COUNTRIES.filter((c) => c.main).map((c) => [c.dial, c.iso]));
// Los indicativos no son prefijo unos de otros: el más largo que coincida es el correcto.
const DIALS_LONGEST_FIRST = [...MAIN_BY_DIAL.keys()].sort((a, b) => b.length - a.length);

/**
 * Primeros de la lista: Colombia y los países con más reservas y consultas web
 * (WhatsApp con referencia web, 14-sep a 14-oct-2026). El resto va en orden alfabético.
 */
export const PREFERRED_PHONE_COUNTRIES = ['CO', 'US', 'ES', 'BR', 'AR', 'PE', 'MX', 'CA', 'CL', 'EC', 'GB', 'FR', 'DE', 'IT', 'NL'] as const;

export function phoneCountry(iso: string): PhoneCountry | undefined {
  return BY_ISO.get(iso);
}

export function dialCodeFor(iso: string): string {
  return BY_ISO.get(iso)?.dial ?? '57';
}

/** Bandera emoji a partir del ISO (indicadores regionales). */
export function flagEmoji(iso: string): string {
  if (!/^[A-Z]{2}$/.test(iso)) return '🏳️';
  return String.fromCodePoint(...[...iso].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

/**
 * Separa el indicativo de un número con «+» o «00». Devuelve `null` mientras el indicativo
 * no esté completo (por ejemplo «+3» o «+35»). Si el país elegido comparte el indicativo
 * (Canadá con +1), lo conserva en lugar de cambiarlo por el país principal.
 */
export function splitInternational(
  text: string,
  currentIso?: string,
): { iso: string; dial: string; national: string } | null {
  const trimmed = text.trim();
  const match = /^(\+|00)/.exec(trimmed);
  if (!match) return null;
  const digits = trimmed.slice(match[0].length).replace(/\D/g, '');
  const dial = DIALS_LONGEST_FIRST.find((d) => digits.startsWith(d));
  if (!dial) return null;
  const current = currentIso ? BY_ISO.get(currentIso) : undefined;
  const iso = current && current.dial === dial ? current.iso : (MAIN_BY_DIAL.get(dial) as string);
  // Conserva el formato que escribió la persona después del indicativo (espacios, guiones).
  let consumed = 0;
  let index = match[0].length;
  while (index < trimmed.length && consumed < dial.length) {
    if (/\d/.test(trimmed[index])) consumed++;
    index++;
  }
  return { iso, dial, national: trimmed.slice(index).replace(/^[\s\-().]+/, '') };
}

/** Separa un número E.164 en indicativo y número nacional (para Bold). */
export function splitE164(value: string): { dial: string; national: string } | null {
  const digits = String(value ?? '').trim().replace(/^\+/, '').replace(/\D/g, '');
  if (!String(value ?? '').trim().startsWith('+')) return null;
  const dial = DIALS_LONGEST_FIRST.find((d) => digits.startsWith(d));
  if (!dial) return null;
  return { dial, national: digits.slice(dial.length) };
}

let namesCache: { locale: string; names: Map<string, string> } | null = null;

/** Nombre del país en el idioma de la página; si el navegador no lo soporta, el ISO. */
export function countryName(iso: string, locale: 'es' | 'en'): string {
  if (!namesCache || namesCache.locale !== locale) {
    namesCache = { locale, names: new Map() };
  }
  const cached = namesCache.names.get(iso);
  if (cached) return cached;
  let name = iso;
  try {
    const display = new Intl.DisplayNames([locale], { type: 'region' });
    name = display.of(iso) ?? iso;
  } catch {
    // Intl.DisplayNames no existe en navegadores antiguos: el ISO sigue siendo elegible.
  }
  namesCache.names.set(iso, name);
  return name;
}
